import * as React from 'react';

import Slider from 'react-range-slider-input';

import 'react-range-slider-input/dist/style.css';

import BaseFilterComponent from '@common/react/components/Core/BaseFilterComponent/BaseFilterComponent';
import SearchWithCheckboxes from '@common/react/components/UI/SearchWithCheckboxes/SearchWithCheckboxes';

import { Gender } from '@commonTuna/react/objects/Enums';
import { Ethnicity } from '@commonTuna/react/objects/Ethnicity';
import { Page } from '@commonTuna/react/objects/Page';

export const initialSliderFilters = {
	ageMin: 20,
	ageMax: 65,
	weightMin: 80,
	weightMax: 300,
	heightMin: 4,
	heightMax: 7,
};

export const initialFilterState: Filter = {
	...initialSliderFilters,
	genders: [],
	ethnicities: [],
	pageIds: [],
	indexInCompany: '',
};

interface ServiceSelectPage extends Page {
	caseCount: number;
}

export interface Filter {
	ageMin: number;
	ageMax: number;
	weightMin: number;
	weightMax: number;
	heightMin: number;
	heightMax: number;
	genders: Array<Gender>;
	ethnicities: Array<number>;
	// doctorId: number;
	pageIds: Array<number>;
	indexInCompany: string;
}

const SliderFilter = (props: {
	filter: Filter;
	min: number;
	max: number;
	field: string;
	title: string;
	changeCallback: (value: Array<number>, field: string) => void;
	submitCallback: (field: string) => void;
	step?: number;
	formatter?: (value: number) => string;
}) => {
	const {
		filter, min, max, field, changeCallback, submitCallback, title, step, formatter,
	} = props;

	const valueMin = formatter ? formatter(filter[`${field}Min`]) : filter[`${field}Min`];
	const valueMax = formatter ? formatter(filter[`${field}Max`]) : filter[`${field}Max`];

	return <div className="single-filter">
		<div className="single-filter__header">
			<div className="single-filter__title">{title}</div>
		</div>
		<div className="pull-left">
			Min:
			{valueMin}
		</div>
		<div className="pull-right">
			Max:
			{valueMax}
		</div>
		<br />
		<div className="slider-wrapper">
			<Slider
				range
				min={min}
				max={max}
				step={step || 1}
				value={[filter[`${field}Min`], filter[`${field}Max`]]}
				onInput={(value) => {
					changeCallback(value as Array<number>, field);
				}}
				onThumbDragEnd={() => {
					submitCallback(field);
				}}
			/>
		</div>
	</div>;
};

interface ComponentProps {
	initialValues: any;
}

type Props = ComponentProps;

const BeforeAfterFilters = (props: Props) => {
	return (<BaseFilterComponent
		initValues={props.initialValues}
		loadFiltersRequest="caseListBbyFilterRemote"
		render={({
			state: { filters }, actions: { handleChange }, values, setValues,
		}) => {
			const [sliderFilters, setSliderFilters] = React.useState<any>({
				ageMin: filters.ageMin,
				ageMax: filters.ageMax,
				weightMin: filters.weightMin,
				weightMax: filters.weightMax,
				heightMin: filters.heightMin,
				heightMax: filters.heightMax,
			});

			const handleSliderChange = (values: Array<number>, field: string) => {
				setSliderFilters((prevState) => ({
					...prevState,
					...filters,
					[`${field}Min`]: values[0],
					[`${field}Max`]: values[1],
				}));
			};

			const handleSliderSubmit = (field: string) => {
				setSliderFilters((prevState) => {
					handleChange({
						[`${field}Min`]: prevState[`${field}Min`],
						[`${field}Max`]: prevState[`${field}Max`],
					});
					return ({
						...prevState,
						...filters,
						[`${field}Min`]: prevState[`${field}Min`],
						[`${field}Max`]: prevState[`${field}Max`],
					});
				});
			};

			const handleGenderChange = (value: Gender) => {
				let genders = filters.genders;
				genders = genders.indexOf(value) === -1 ? [...genders, value] : genders.filter((g) => g !== value);

				handleChange({ genders });
			};

			const handleEthnicityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
				const id = +event.target.value;
				const ethnicities = filters.ethnicities.indexOf(id) === -1
					? [...filters.ethnicities, id]
					: filters.ethnicities.filter((i) => i !== id);

				handleChange({ ethnicities });
			};

			return <>
				<SearchWithCheckboxes<ServiceSelectPage>
					titleClassName="single-filter__title"
					className="single-filter"
					title="Services"
					type="serviceListRemote"
					loadRequest="serviceSelectListRemote"
					values={filters.pageIds}
					initLoadedAll
					initItems={values?.pages}
					renderName={(item) => `${item.name} (${item.caseCount})`}
					onChange={(values) => {
						handleChange({ pageIds: values?.map((q) => +q) });
					}}
				/>
				<SliderFilter
					filter={sliderFilters}
					min={18}
					max={100}
					field="age"
					title="Age"
					changeCallback={handleSliderChange}
					submitCallback={handleSliderSubmit}
				/>
				<SliderFilter
					filter={sliderFilters}
					min={80}
					max={300}
					field="weight"
					title="Weight"
					changeCallback={handleSliderChange}
					submitCallback={handleSliderSubmit}
				/>
				<SliderFilter
					filter={sliderFilters}
					min={4}
					max={7}
					step={0.05}
					field="height"
					title="Height"
					changeCallback={handleSliderChange}
					submitCallback={handleSliderSubmit}
					formatter={(value) => `${Math.floor(value)}' ${Math.floor((value - Math.floor(value)) * 10) % 12}"`}
				/>
				<div className="single-filter single-filter_gender">
					<div className="single-filter__title">Gender</div>
					<div>
						<button
							type="button"
							className={`general-form-button general-form-button_sm ${filters.genders.length
							&& filters.genders.indexOf(Gender.Male) !== -1
							&& 'general-form-button_selected'}`
							}
							onClick={() => { handleGenderChange(Gender.Male); }}
						>
							<i className="fa fa-mars" />
							{' '}
							Male
						</button>
						<button
							type="button"
							className={`general-form-button general-form-button_sm ${filters.genders.length
							&& filters.genders.indexOf(Gender.Female) !== -1
							&& 'general-form-button_selected'}`
							}
							onClick={() => { handleGenderChange(Gender.Female); }}
						>
							<i className="fa fa-venus" />
							{' '}
							Female
						</button>
						<button
							type="button"
							className={`general-form-button general-form-button_sm ${filters.genders.length
							&& filters.genders.indexOf(Gender.Other) !== -1
							&& 'general-form-button_selected'}`}
							onClick={() => { handleGenderChange(Gender.Other); }}
						>
							<i className="fa fa-transgender" />
							{' '}
							Other
						</button>
					</div>
				</div>
				<div className="single-filter">
					<div className="single-filter__title">Ethnicity</div>
					<div className="single-filter__ethnicities">
						{values?.ethnicities && values.ethnicities.length > 0 && values.ethnicities.map((ethnicity: Ethnicity, index: number) => {
							return <div key={ethnicity.id}>
								<label htmlFor={`checkbox-${index}`}>
									<input
										type="checkbox"
										id={`checkbox-${index}`}
										value={ethnicity.id}
										onChange={handleEthnicityChange}
										checked={filters.ethnicities.indexOf(ethnicity.id) !== -1}
									/>
									<span className="fa fa-check">{ethnicity.nameEn}</span>
								</label>
							</div>;
						})}
					</div>
				</div>
			</>;
		}}
	/>);
};

export default BeforeAfterFilters;
